import { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import PageTitle from "components/text/PageTitle";
import RoundActionIcon from "components/buttons/RoundActionIcon";
import ButtonRoundIcon from "components/buttons/ButtonRoundIcon";
import ButtonPrimarySmall from "components/buttons/ButtonPrimarySmall";
import ButtonPrimarySmallIcon from "components/buttons/ButtonPrimarySmallIcon";
import Tip from "components/other/Tip";
import ToggleSwitch from "components/forms/ToggleSwitch";

import returnIcon from "assets/img/icons/return-icon.svg";
import userCircleIcon from "assets/img/icons/user-single-icon.svg";
import userCircleWhiteIcon from "assets/img/icons/user-single-icon-white.svg";
import percentWhiteIcon from "assets/img/icons/percent-icon-white.svg";
import rightUpArrowIcon from "assets/img/icons/icons8-right-up-arrow.svg";
import rightUpArrowWhiteIcon from "assets/img/icons/icons8-right-up-arrow-white.svg";
import pencilIcon from "assets/img/icons/pencil-icon.svg";
import pencilWhiteIcon from "assets/img/icons/pencil-icon-white.svg";
import { numberWithCommas } from "modules/functions";

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BackIconWrap = styled.div``;

const MailingListDataBox = styled.div`
  position: relative;
  width: 100%;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  flex-direction: row;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};

  @media (max-width: ${globalConstants.switchMobile}) {
    flex-direction: column;
  }
`;

const LeftBoxColumn = styled.div`
  flex: 0.5;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const StatsBoxTitle = styled.div`
  font-weight: 500;
`;

const StatsBox = styled.div`
  padding: 15px 20px;
  width: 100%;
  box-sizing: border-box;

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  display: grid;
  grid-template-columns: 37px 73px 73px 1fr;
  grid-template-rows: 25px 1fr 1fr 1fr;
`;

const BoxLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
`;

const LabelIconCell = styled.div`
  display: flex;
  align-items: center;
`;

const LabelIcon = styled.div`
  width: 16px;
  min-width: 16px;
  height: 16px;
`;

const ValueCell = styled.div`
  height: 24px;

  font-size: 13px;

  display: flex;
  align-items: center;
`;

const ChangeBadge = styled.div`
  height: 16px;
  padding: 0 4px 1px;
  margin-left: 5px;

  font-size: 11px;

  border-radius: 8px;
  background: ${(props) =>
    props.green ? css.POSITIVE_GREEN : css.NEGATIVE_RED};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const EditButtonWrap = styled.div`
  display: flex;
`;

const RightBoxColumn = styled.div`
  flex: 0.5;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const RightBoxColumnData = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const RightBoxColumnDataTitle = styled.div`
  font-weight: 500;

  display: flex;
  align-items: center;
  gap: 6px;
`;

const RightBoxColumnDataContent = styled.div``;

const ConnectedPagesList = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 6px;
`;

const ConnectedPage = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const ConnectedPageName = styled.div`
  line-height: 1;
`;

const ConnectedPageButtonWrap = styled.div``;

const NoPageConnected = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const Badge = styled.div`
  height: 20px;
  padding: 0 14px 1px;

  font-size: 13px;
  color: #fff;
  white-space: nowrap;

  border-radius: 10px;
  background: ${css.NEGATIVE_RED};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const AddLandingPageButtonWrap = styled.div`
  margin-top: 6px;
`;

const NotificationsLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const NotificationsLabel = styled.div`
  line-height: 1;
`;

export default ({
  data,
  onPageClick,
  notifications,
  onNotificationsToggleClick,
  loadingNotificationsToggle,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const from = searchParams.get("from") ? searchParams.get("from") : null;
  const page = searchParams.get("page") ? searchParams.get("page") : null;

  // set page Title
  useEffect(() => {
    document.title = "Mailing List | Scalelup";
  }, []);

  const changeBadge = (change) => {
    if (typeof change !== "number" || change === 0) return <></>;

    if (change > 100) return <ChangeBadge green={true}>&gt;100%</ChangeBadge>;

    const roundCoef = change >= 10 || change <= -10 ? 1 : 10;
    let changeRounded = Math.round(change * roundCoef) / roundCoef;

    if (changeRounded >= 0)
      return <ChangeBadge green={true}>+{changeRounded}%</ChangeBadge>;
    else return <ChangeBadge green={false}>{changeRounded}%</ChangeBadge>;
  };

  const {
    last24HoursLeads,
    last24HoursVerifiedLeads,
    prev24HoursLeads,
    prev24HoursVerifiedLeads,
    last7DaysLeads,
    last7DaysVerifiedLeads,
    prev7DaysLeads,
    prev7DaysVerifiedLeads,
    allLeads,
    allVerifiedLeads,
  } = data.mailingListData;

  const last24HoursLeadsChange =
    prev24HoursLeads > 0
      ? (100 / prev24HoursLeads) * last24HoursLeads - 100
      : "-";

  const last7DaysLeadsChange =
    prev7DaysLeads > 0 ? (100 / prev7DaysLeads) * last7DaysLeads - 100 : "-";

  const last24HoursVerifiedLeadsChange =
    prev24HoursVerifiedLeads > 0
      ? (100 / prev24HoursVerifiedLeads) * last24HoursVerifiedLeads - 100
      : "-";

  const last7DaysVerifiedLeadsChange =
    prev7DaysVerifiedLeads > 0
      ? (100 / prev7DaysVerifiedLeads) * last7DaysVerifiedLeads - 100
      : "-";

  const last24hoursVerifiedPercent =
    last24HoursLeads > 0
      ? Math.round((100 / last24HoursLeads) * last24HoursVerifiedLeads * 10) /
        10
      : 0;

  const last7DaysVerifiedPercent =
    last7DaysLeads > 0
      ? Math.round((100 / last7DaysLeads) * last7DaysVerifiedLeads * 10) / 10
      : 0;

  const allVerifiedPercent =
    allLeads > 0
      ? Math.round((100 / allLeads) * allVerifiedLeads * 10) / 10
      : 0;

  // prepare Connected pages list
  const connectedPages = data.mailingListData.list.pages.map((page) => {
    return (
      <ConnectedPage>
        <ConnectedPageName>{page.name}</ConnectedPageName>
        <ConnectedPageButtonWrap>
          <ButtonRoundIcon
            icon={rightUpArrowIcon}
            hoverIcon={rightUpArrowWhiteIcon}
            background={css.BRAND_YELLOW}
            hoverBackground={css.BRAND_GREEN}
            onClick={() => onPageClick(page.code)}
            buttonSize="22px"
            iconSize="10px"
          />
        </ConnectedPageButtonWrap>
      </ConnectedPage>
    );
  });

  let returnTo = "/mailing-lists";

  if (from === "marketing-tools") returnTo = "/marketing-tools";
  if (from === "landing-pages") {
    returnTo = "/landing-pages";

    if (page) returnTo += `?page=${page}`;
  }

  const { lead_notification } = data.mailingListData.list;

  return (
    <>
      <Header>
        <PageTitle>{data.mailingListData.list.name}</PageTitle>
        <BackIconWrap>
          <RoundActionIcon
            icon={returnIcon}
            iconSize="16px"
            iconAlt="Return"
            onClick={() => navigate(returnTo)}
          />
        </BackIconWrap>
      </Header>
      <MailingListDataBox>
        <LeftBoxColumn>
          <StatsBoxTitle>List Stats</StatsBoxTitle>
          <StatsBox>
            <BoxLabel></BoxLabel>
            <BoxLabel>24 Hours</BoxLabel>
            <BoxLabel>7 Days</BoxLabel>
            <BoxLabel>Total</BoxLabel>
            <LabelIconCell>
              <LabelIcon>
                <img src={userCircleIcon} style={{ width: "100%" }} />
              </LabelIcon>
            </LabelIconCell>
            <ValueCell>
              {numberWithCommas(last24HoursLeads)}
              {changeBadge(last24HoursLeadsChange)}
            </ValueCell>
            <ValueCell>
              {numberWithCommas(last7DaysLeads)}
              {changeBadge(last7DaysLeadsChange)}
            </ValueCell>
            <ValueCell>{numberWithCommas(allLeads)}</ValueCell>
            <LabelIconCell>
              <LabelIcon>
                <img src={userCircleWhiteIcon} style={{ width: "100%" }} />
              </LabelIcon>
            </LabelIconCell>
            <ValueCell>
              {numberWithCommas(last24HoursVerifiedLeads)}
              {changeBadge(last24HoursVerifiedLeadsChange)}
            </ValueCell>
            <ValueCell>
              {numberWithCommas(last7DaysVerifiedLeads)}
              {changeBadge(last7DaysVerifiedLeadsChange)}
            </ValueCell>
            <ValueCell>{numberWithCommas(allVerifiedLeads)}</ValueCell>
            <LabelIconCell>
              <LabelIcon>
                <img src={percentWhiteIcon} style={{ width: "100%" }} />
              </LabelIcon>
            </LabelIconCell>
            <ValueCell>{last24hoursVerifiedPercent}%</ValueCell>
            <ValueCell>{last7DaysVerifiedPercent}%</ValueCell>
            <ValueCell>{allVerifiedPercent}%</ValueCell>
          </StatsBox>
          <NotificationsLine>
            <ToggleSwitch
              on={lead_notification}
              onClick={onNotificationsToggleClick}
              loading={loadingNotificationsToggle}
            />
            <NotificationsLabel>Lead notifications</NotificationsLabel>
            <Tip
              tipId="leadNotifications"
              tip="Choose whether you want to receive an email notification whenever you have a new lead in this list.
"
            />
          </NotificationsLine>
          <EditButtonWrap>
            <ButtonPrimarySmallIcon
              icon={pencilIcon}
              iconHover={pencilWhiteIcon}
              onClick={() =>
                navigate(
                  `/create-mailing-list?id=${data.mailingListData.list.id}`
                )
              }
            >
              Edit List
            </ButtonPrimarySmallIcon>
          </EditButtonWrap>
        </LeftBoxColumn>
        <RightBoxColumn>
          <RightBoxColumnData>
            <RightBoxColumnDataTitle>Connected Pages</RightBoxColumnDataTitle>
            <RightBoxColumnDataContent>
              <ConnectedPagesList>
                {connectedPages.length > 0 && connectedPages}
                {connectedPages.length === 0 && (
                  <NoPageConnected>
                    <Badge>No page connected</Badge>
                    <Tip
                      tipId="noPage"
                      tip="No landing page is currently saving contacts to this list."
                      position="bottom"
                    />
                  </NoPageConnected>
                )}
                <AddLandingPageButtonWrap>
                  <ButtonPrimarySmall
                    onClick={() => navigate("/landing-pages")}
                  >
                    Add Landing Page
                  </ButtonPrimarySmall>
                </AddLandingPageButtonWrap>
              </ConnectedPagesList>
            </RightBoxColumnDataContent>
          </RightBoxColumnData>
          <RightBoxColumnData>
            <RightBoxColumnDataTitle>Autoresponder</RightBoxColumnDataTitle>
            <RightBoxColumnDataContent>
              Not set (coming soon)
            </RightBoxColumnDataContent>
          </RightBoxColumnData>
        </RightBoxColumn>
      </MailingListDataBox>
    </>
  );
};
